import { ChangeDetectorRef, Component, inject, input, OnChanges, SimpleChanges } from '@angular/core';
import { ZiroFieldAbstractDirective } from '../../../../forms/ziro-field-abstract.directive';
import { NaturalLanguageSingleOption } from '../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-single/natural-language-editor-single.component';
import { TranslateService } from '@ngx-translate/core';
import { NaturalLanguageEditorDoublePrimaryDropdownOption } from '../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-double/natural-language-editor-double.component';
import { map, Observable, of } from 'rxjs';
import { NumberSearchResult, UsernameSearchResult } from '../../../../shared/models/generated/ziroModels';
import {
  DetailedCallRecordDirection,
  DetailedCallRecordsReportResource,
} from '../../shared/detailed-call-records-report.resource';
import { NaturalLanguageEditorEntityValidation } from '../../../../shared/natural-language-editor/natural-language-editor.component';

interface CallRecordFieldData {
  callDirectionType: CallRecordSearchDirectionType;
  searchValue: {
    primary: CallRecordSearchType;
    secondary: string;
  };
  fromDate?: Date;
  toDate?: Date;
}

export interface CallRecordEntity {
  callDirectionType: DetailedCallRecordDirection;
  callRecordSearchType: CallRecordSearchType;
  searchValue: string;
  fromDate?: Date;
  toDate?: Date;
}

export enum CallRecordSearchDirectionType {
  ALL = 'All',
  PLACED = 'PLACED',
  RECEIVED = 'RECEIVED',
}

export enum CallRecordSearchType {
  USER_PRINCIPAL_NAME = 'userPrincipalName',
  DISPLAY_NAME = 'displayName',
  NUMBER = 'number',
}

@Component({
  selector: 'ziro-pstn-call-record-query-editor-field',
  templateUrl: './pstn-call-record-query-editor-field.component.html',
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: PstnCallRecordQueryEditorFieldComponent }],
  standalone: false,
})
export class PstnCallRecordQueryEditorFieldComponent
  extends ZiroFieldAbstractDirective<CallRecordEntity, CallRecordFieldData>
  implements OnChanges
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private _detailedCallRecordsResource = inject(DetailedCallRecordsReportResource);
  private _translateService = inject(TranslateService);

  disabledDateTooltipText = input<string>('');
  minDateInString = input<string>('');
  tenantId = input<string>();

  callDirectionOptions: NaturalLanguageSingleOption[];
  callRecordSearchTypeOptions: NaturalLanguageEditorDoublePrimaryDropdownOption[];
  hasValidationError = false;
  minDate: Date;
  nleEntityValidators: NaturalLanguageEditorEntityValidation<CallRecordFieldData>[] = [
    {
      key: 'date-range',
      message: 'tkey;reports.microsoft.pstn_call_record_finder.query.invalid_range.message',
      validator: (entity) => {
        const from = entity?.fromDate ? new Date(entity.fromDate).getTime() : null;
        const to = entity?.toDate ? new Date(entity.toDate).getTime() : null;
        return from == null || to == null || from <= to;
      },
    },
  ];

  get dateRangeInvalid(): boolean {
    const from = this.fieldData?.fromDate ? new Date(this.fieldData.fromDate).getTime() : null;
    const to = this.fieldData?.toDate ? new Date(this.fieldData.toDate).getTime() : null;
    return from != null && to != null && from > to;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fieldId?.currentValue) {
      this._setNaturalLanguageEditorConfig();
    }
    if (
      changes.minDateInString.currentValue &&
      changes.minDateInString.previousValue !== changes.minDateInString.currentValue
    ) {
      this.minDate = this._parseBackendDateTime(changes.minDateInString.currentValue);
    }
  }

  updateSelf(newFieldData: CallRecordFieldData, markAsDirty = true, oldFieldData?: CallRecordFieldData): void {
    const isSearchChanged = this.fieldData && this.fieldData.searchValue.primary !== newFieldData.searchValue.primary;
    const isSecondaryChanged =
      this.fieldData && this.fieldData.searchValue.secondary !== newFieldData.searchValue.secondary;
    super.updateSelf(newFieldData, markAsDirty, oldFieldData);
    this.changeDetectorRef.detectChanges();
    if (isSearchChanged) {
      newFieldData.searchValue = {
        primary: newFieldData.searchValue.primary,
        secondary: isSecondaryChanged ? newFieldData.searchValue.secondary : '',
      };
      super.updateSelf(newFieldData, markAsDirty, oldFieldData);
    }
  }

  updateValidationState(hasValidationError: boolean): void {
    this.hasValidationError = hasValidationError;
  }

  toEntity = (fieldData: CallRecordFieldData): CallRecordEntity => {
    return {
      callDirectionType: this._mapToEntityDirectionType(fieldData),
      callRecordSearchType: fieldData.searchValue.primary,
      searchValue: fieldData.searchValue.secondary || '',
      fromDate: fieldData.fromDate ?? null,
      toDate: fieldData.toDate ?? null,
    };
  };

  toFieldData = (entity: CallRecordEntity): CallRecordFieldData => {
    return {
      callDirectionType: this._mapToFieldDataDirectionType(entity),
      searchValue: {
        primary: entity.callRecordSearchType,
        secondary: entity.searchValue,
      },
      fromDate: entity.fromDate ?? null,
      toDate: entity.toDate ?? null,
    };
  };

  private _getUniqueAndSortedOptions(options: string[]): string[] {
    const uniqueOptions = Array.from(new Set(options.map((option: string) => option)).values());
    return uniqueOptions.sort((a, b) => a.localeCompare(b));
  }

  private _mapToEntityDirectionType(fieldData: CallRecordFieldData): DetailedCallRecordDirection {
    switch (fieldData.callDirectionType) {
      case CallRecordSearchDirectionType.ALL:
        return DetailedCallRecordDirection.BOTH;
      case CallRecordSearchDirectionType.PLACED:
        return DetailedCallRecordDirection.OUTBOUND;
      case CallRecordSearchDirectionType.RECEIVED:
        return DetailedCallRecordDirection.INBOUND;
    }
  }

  private _mapToFieldDataDirectionType(entity: CallRecordEntity): CallRecordSearchDirectionType {
    switch (entity.callDirectionType) {
      case DetailedCallRecordDirection.BOTH:
        return CallRecordSearchDirectionType.ALL;
      case DetailedCallRecordDirection.INBOUND:
        return CallRecordSearchDirectionType.RECEIVED;
      case DetailedCallRecordDirection.OUTBOUND:
        return CallRecordSearchDirectionType.PLACED;
    }
  }

  private _parseBackendDateTime(dateTime: string): Date {
    const trimmedDateTime = (dateTime ?? '').trim();
    if (!trimmedDateTime) {
      return;
    }
    const isoLike = trimmedDateTime.replace(' ', 'T');
    const date = new Date(isoLike);
    if (Number.isNaN(date.getTime())) {
      return;
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private _searchDisplayName(q: string): Observable<string[]> {
    return this._detailedCallRecordsResource.userPrincipalNamesSearch(this.tenantId(), q).pipe(
      map((results: UsernameSearchResult[]) => {
        if (!results?.length) {
          return;
        }
        const displayNames: string[] = results.map((result: UsernameSearchResult) => result.displayName);
        return this._getUniqueAndSortedOptions(displayNames);
      }),
    );
  }

  private _searchNumbers(q: string): Observable<string[]> {
    if (typeof q !== 'string') {
      return of([]);
    }
    return this._detailedCallRecordsResource.numbersSearch(this.tenantId(), q).pipe(
      map((results: NumberSearchResult[]) => {
        if (!results?.length) {
          return;
        }
        const numbers: string[] = results.map((result: NumberSearchResult) => result.number);
        return this._getUniqueAndSortedOptions(numbers);
      }),
    );
  }

  private _searchUserPrincipalName(q: string): Observable<string[]> {
    return this._detailedCallRecordsResource.userPrincipalNamesSearch(this.tenantId(), q).pipe(
      map((results: UsernameSearchResult[]) => {
        if (!results?.length) {
          return;
        }
        const usernames: string[] = results.map((result: UsernameSearchResult) => result.userPrincipalName);
        return this._getUniqueAndSortedOptions(usernames);
      }),
    );
  }

  private _setNaturalLanguageEditorConfig(): void {
    this.callDirectionOptions = [
      {
        value: CallRecordSearchDirectionType.ALL,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.all',
        ),
      },
      {
        value: CallRecordSearchDirectionType.PLACED,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.placed',
        ),
      },
      {
        value: CallRecordSearchDirectionType.RECEIVED,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.received',
        ),
      },
    ];
    this.callRecordSearchTypeOptions = [
      {
        value: CallRecordSearchType.USER_PRINCIPAL_NAME,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.user_principal_name',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.user_principal_name.placeholder',
          ),
          placeholder: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.user_principal_name.placeholder',
          ),
          searchFn: (searchTerm) => this._searchUserPrincipalName(searchTerm),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message:
                'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.user_principal_name.required_validator',
            },
          ],
        },
      },
      {
        value: CallRecordSearchType.NUMBER,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.number',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.number.placeholder',
          ),
          placeholder: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.number.placeholder',
          ),
          searchFn: (searchTerm) => this._searchNumbers(searchTerm),
          minSearchLength: 5,
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message:
                'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.number.required_validator',
            },
          ],
        },
      },
      {
        value: CallRecordSearchType.DISPLAY_NAME,
        label: this._translateService.instant(
          'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.displayName',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.displayName.placeholder',
          ),
          placeholder: this._translateService.instant(
            'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.displayName.placeholder',
          ),
          searchFn: (searchTerm) => this._searchDisplayName(searchTerm),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message:
                'tkey;reports.microsoft.pstn_call_record_finder.query.call_direction.type.displayName.required_validator',
            },
          ],
        },
      },
    ];
  }
}
